import { IMAGE_DIMENSIONS } from './constants';

export interface CropArea {
	x: number;
	y: number;
	width: number;
	height: number;
}

/**
 * Produces a cropped image file from the given source URL and pixel-based crop area.
 * The output is always sized to IMAGE_DIMENSIONS (1080x566) so it can be used
 * directly as the testimonial background without further scaling.
 *
 * Expects a data URL or same-origin blob URL to avoid canvas taint / CORS issues.
 * Use useGetImageUrl to resolve remote attachment URLs before calling this.
 */
export const cropImage = (imageUrl: string, cropArea: CropArea, fileName = 'cropped-background.png'): Promise<File> =>
	new Promise((resolve, reject) => {
		const img = new Image();

		img.onload = () => {
			const canvas = document.createElement('canvas');
			canvas.width = IMAGE_DIMENSIONS.width;
			canvas.height = IMAGE_DIMENSIONS.height;
			const ctx = canvas.getContext('2d');
			if (!ctx) {
				reject(new Error('Could not get canvas context'));
				return;
			}

			ctx.drawImage(
				img,
				cropArea.x,
				cropArea.y,
				cropArea.width,
				cropArea.height,
				0,
				0,
				IMAGE_DIMENSIONS.width,
				IMAGE_DIMENSIONS.height
			);

			canvas.toBlob(blob => {
				if (!blob) {
					reject(new Error('Failed to create blob from canvas'));
					return;
				}
				resolve(new File([blob], fileName, { type: 'image/png' }));
			}, 'image/png');
		};

		img.onerror = () => reject(new Error('Failed to load image for cropping'));
		img.src = imageUrl;
	});

/**
 * Adds a color overlay to an image file. Used for testimonial background images
 * to improve text readability over selected images.
 */
export const addColorOverlayToImage = (file: File, overlayColor: string, opacity: number): Promise<File> =>
	new Promise((resolve, reject) => {
		const img = new Image();
		const url = URL.createObjectURL(file);

		img.onload = () => {
			URL.revokeObjectURL(url);
			const canvas = document.createElement('canvas');
			canvas.width = img.width;
			canvas.height = img.height;
			const ctx = canvas.getContext('2d');
			if (!ctx) {
				reject(new Error('Could not get canvas context'));
				return;
			}
			ctx.drawImage(img, 0, 0);
			ctx.fillStyle = overlayColor;
			ctx.globalAlpha = opacity;
			ctx.fillRect(0, 0, canvas.width, canvas.height);
			ctx.globalAlpha = 1;

			canvas.toBlob(blob => {
				if (!blob) {
					reject(new Error('Failed to create blob from canvas'));
					return;
				}
				resolve(new File([blob], file.name, { type: 'image/png' }));
			}, 'image/png');
		};
		img.onerror = () => {
			URL.revokeObjectURL(url);
			reject(new Error('Failed to load image'));
		};
		img.src = url;
	});

/** Default overlay color for testimonial background images (helps with readability.) */
export const TESTIMONIAL_OVERLAY_COLOR = '#505050';
/** Default overlay opacity for testimonial background images. */
export const TESTIMONIAL_OVERLAY_OPACITY = 0.4;
